import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { User, Session } from "@supabase/supabase-js";
import {
  resolveAccountType,
  resolveAdminRole,
  type AccountType,
  type AdminRole,
  type SignupAccountType,
} from "@/lib/admin-access";
import { normalizeAccessSnapshot, type AccessSnapshot } from "@/lib/access-control";
import {
  getAuthEmailRedirectTo,
  getPasswordResetRedirectTo,
  isDuplicateSignupError,
  isLikelyDuplicateSignup,
  normalizeAuthEmail,
} from "@/lib/auth-security";
import { identifyProductUser, resetProductUser } from "@/lib/analytics";
import { clearMonitoringUser, setMonitoringUser } from "@/lib/monitoring";
import { reportClientError } from "@/lib/client-error-reporting";
import { writeSelectedVenueContext } from "@/lib/venue-selection";
import { useConsent } from "@/hooks/useConsent";
import { privacyService } from "@/services/api";
import type { ConsentData } from "@/lib/consent.utils";

type UserProfile = Tables<"profiles">;

interface LegalAcceptancePayload {
  version: string;
  accepted_at: string;
  documents: Array<{ label: string; href: string }>;
}

interface AuthCaptchaOptions {
  captchaToken?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  accountType: AccountType;
  adminRole: AdminRole | null;
  isPlatformAdmin: boolean;
  accessSnapshot: AccessSnapshot | null;
  roles: string[];
  tenantRoles: { tenant_id: string; role: string }[];
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    options?: { accountType?: SignupAccountType; legalAcceptance?: LegalAcceptancePayload; captchaToken?: string },
  ) => Promise<{ error: Error | null; duplicateEmail: boolean; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string, options?: AuthCaptchaOptions) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, options?: AuthCaptchaOptions) => Promise<{ error: Error | null }>;
  hasRole: (role: string) => boolean;
  isTenantAdmin: (tenantId: string) => boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [tenantRoles, setTenantRoles] = useState<{ tenant_id: string; role: string }[]>([]);
  const [accessSnapshot, setAccessSnapshot] = useState<AccessSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const accountType = accessSnapshot?.account_type ?? resolveAccountType(user?.user_metadata, roles, tenantRoles);
  const adminRole = accessSnapshot?.admin_role ?? resolveAdminRole(user?.user_metadata, roles);
  const isPlatformAdmin = accessSnapshot?.permissions.can_access_admin ?? roles.includes("platform_admin");
  const lastSessionFingerprintRef = useRef<string>("__boot__");
  const lastUserDataRefreshAtRef = useRef<number>(0);
  const isRefreshingUserDataRef = useRef(false);
  const { isAllowed } = useConsent();
  const analyticsAllowed = isAllowed("analytics");

  const isInvalidRefreshTokenError = useCallback((error: unknown) => {
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "";

    return /invalid refresh token|refresh token not found/i.test(message);
  }, []);

  const clearAuthState = useCallback(() => {
    setSession(null);
    setUser(null);
    setProfile(null);
    setRoles([]);
    setTenantRoles([]);
    setAccessSnapshot(null);
    writeSelectedVenueContext(null);
  }, []);

  const ensureValidSession = useCallback(async (nextSession: Session | null) => {
    if (!nextSession?.access_token) {
      return null;
    }

    const validateAccessToken = async (accessToken: string, fallbackUser?: User | null) => {
      try {
        const { data, error } = await supabase.auth.getUser(accessToken);
        if (error || !data.user) {
          return null;
        }

        return data.user;
      } catch (error) {
        console.warn("Transient auth validation failure, preserving local session.", error);
        return fallbackUser ?? null;
      }
    };

    const currentUser = await validateAccessToken(nextSession.access_token, nextSession.user);
    if (currentUser) {
      return {
        ...nextSession,
        user: currentUser,
      };
    }

    if (!nextSession.refresh_token) {
      await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      return null;
    }

    try {
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: nextSession.refresh_token,
      });

      if (error || !data.session?.access_token) {
        if (!error || isInvalidRefreshTokenError(error)) {
          await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
        }
        return null;
      }

      const refreshedUser = await validateAccessToken(data.session.access_token, data.session.user);
      if (!refreshedUser) {
        await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
        return null;
      }

      return {
        ...data.session,
        user: refreshedUser,
      };
    } catch (error) {
      if (isInvalidRefreshTokenError(error)) {
        await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
        return null;
      }

      console.warn("Transient auth refresh failure, keeping current local session.", error);
      return nextSession.user
        ? {
            ...nextSession,
            user: nextSession.user,
          }
        : null;
    }
  }, [isInvalidRefreshTokenError]);

  const fetchUserData = useCallback(async (userId: string) => {
    try {
      const [profileRes, rolesRes, tenantRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.from("tenant_users").select("tenant_id, role").eq("user_id", userId).eq("status", "active"),
      ]);
      setProfile(profileRes.data ?? null);
      if (rolesRes.data) setRoles(rolesRes.data.map((r) => r.role));
      if (tenantRes.data) setTenantRoles(tenantRes.data.map((t) => ({ tenant_id: t.tenant_id, role: t.role })));

      const { data: accessData, error: accessError } = await supabase.rpc("get_current_user_access");
      if (accessError) {
        if (import.meta.env.DEV) {
          console.warn("Trusted access snapshot unavailable; using role-table fallback.", accessError.message);
        }
        setAccessSnapshot(null);
      } else {
        setAccessSnapshot(normalizeAccessSnapshot(accessData));
      }

      lastUserDataRefreshAtRef.current = Date.now();
    } catch (e) {
      reportClientError("auth.fetch-user-data", e);
    }
  }, []);

  const refreshUserData = useCallback(async () => {
    if (!user?.id) return;
    if (isRefreshingUserDataRef.current) return;

    isRefreshingUserDataRef.current = true;
    try {
      await fetchUserData(user.id);
    } finally {
      isRefreshingUserDataRef.current = false;
    }
  }, [fetchUserData, user?.id]);

  const applySession = useCallback(async (nextSession: Session | null) => {
    const fingerprint =
      nextSession?.access_token ??
      nextSession?.refresh_token ??
      (nextSession?.user?.id ? `user:${nextSession.user.id}` : "__signed_out__");

    if (lastSessionFingerprintRef.current === fingerprint) {
      return;
    }

    lastSessionFingerprintRef.current = fingerprint;
    setLoading(true);

    try {
      const validSession = await ensureValidSession(nextSession);

      setSession(validSession);
      setUser(validSession?.user ?? null);

      if (!validSession?.user) {
        clearAuthState();
        setLoading(false);
        return;
      }

      setProfile(null);
      setRoles([]);
      setTenantRoles([]);
      setAccessSnapshot(null);

      await fetchUserData(validSession.user.id);
      setLoading(false);
    } catch (error) {
      reportClientError("auth.apply-session", error);
      await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      clearAuthState();
      setLoading(false);
    }
  }, [clearAuthState, ensureValidSession, fetchUserData]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Use setTimeout to avoid Supabase client deadlock
      setTimeout(() => {
        void applySession(session);
      }, 0);
    });

    // THEN check for existing session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        void applySession(session);
      })
      .catch((error) => {
        reportClientError("auth.recover-session", error);
        clearAuthState();
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [applySession, clearAuthState]);

  useEffect(() => {
    if (!user) {
      clearMonitoringUser();
      resetProductUser();
      return;
    }

    const primaryVenueMembership = accessSnapshot?.tenant_memberships[0] ?? null;
    const resolvedRole =
      accessSnapshot?.admin_role ??
      primaryVenueMembership?.role ??
      roles[0] ??
      accountType;
    const resolvedPlan =
      accountType === "player"
        ? accessSnapshot?.player_plan.plan.key ?? null
        : primaryVenueMembership?.plan.plan.key ?? null;

    setMonitoringUser({
      id: user.id,
    });

    identifyProductUser({
      id: user.id,
      role: resolvedRole,
      plan: resolvedPlan,
    });
  }, [accessSnapshot, accountType, analyticsAllowed, roles, user]);

  useEffect(() => {
    if (!user?.id || loading) return;

    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") return;

      const elapsed = Date.now() - lastUserDataRefreshAtRef.current;
      if (elapsed < 15_000) return;

      void refreshUserData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshIfStale();
      }
    };

    window.addEventListener("focus", refreshIfStale);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshIfStale);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, refreshUserData, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const handleConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{
        previousConsent: ConsentData | null;
        nextConsent: ConsentData;
      }>).detail;
      if (!detail?.nextConsent) return;

      const previousCategories = detail.previousConsent?.categories ?? null;
      const nextCategories = detail.nextConsent.categories;
      const granted = Object.entries(nextCategories).some(
        ([key, value]) => key !== "essential" && value && previousCategories?.[key as keyof typeof nextCategories] !== true,
      );
      const revoked = Object.entries(nextCategories).some(
        ([key, value]) => key !== "essential" && !value && previousCategories?.[key as keyof typeof nextCategories] === true,
      );

      if (!granted && !revoked) return;

      void privacyService.logConsentChange({
        event_type: revoked ? "consent_revoked" : "consent_granted",
        categories: nextCategories,
      }).catch((error) => {
        reportClientError("auth.log-consent", error);
      });
    };

    window.addEventListener("qc:consent-updated", handleConsentUpdate);
    return () => window.removeEventListener("qc:consent-updated", handleConsentUpdate);
  }, [user?.id]);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    options?: { accountType?: SignupAccountType; legalAcceptance?: LegalAcceptancePayload; captchaToken?: string },
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email: normalizeAuthEmail(email),
      password,
      options: {
        data: {
          full_name: fullName,
          account_type: options?.accountType === "venue" ? "venue" : "player",
          legal_acceptance: options?.legalAcceptance ?? null,
        },
        captchaToken: options?.captchaToken,
        emailRedirectTo: getAuthEmailRedirectTo(window.location.origin),
      },
    });

    const duplicateEmail = isLikelyDuplicateSignup(data) || isDuplicateSignupError(error);
    return {
      error: duplicateEmail ? null : (error as Error | null),
      duplicateEmail,
      needsEmailConfirmation: Boolean(data.user && !data.session && !duplicateEmail),
    };
  };

  const signIn = async (email: string, password: string, options?: AuthCaptchaOptions) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: normalizeAuthEmail(email),
      password,
      options: {
        captchaToken: options?.captchaToken,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn("Error revoking Supabase session, clearing local state instead.", error);
    } finally {
      await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      clearAuthState();
    }
  };

  const resetPassword = async (email: string, options?: AuthCaptchaOptions) => {
    const { error } = await supabase.auth.resetPasswordForEmail(normalizeAuthEmail(email), {
      redirectTo: getPasswordResetRedirectTo(window.location.origin),
      captchaToken: options?.captchaToken,
    });
    return { error: error as Error | null };
  };

  const hasRole = (role: string) => roles.includes(role);
  const isTenantAdmin = (tenantId: string) =>
    tenantRoles.some((t) => t.tenant_id === tenantId && (t.role === "owner" || t.role === "manager"));

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        accountType,
        adminRole,
        isPlatformAdmin,
        accessSnapshot,
        roles,
        tenantRoles,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        hasRole,
        isTenantAdmin,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
